import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Button, Paper, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, LinearProgress, CircularProgress, Alert, Dialog,
  DialogTitle, DialogContent, DialogActions, TextField, MenuItem, IconButton,
  Grid, Card, CardContent, Stack, Divider, Chip
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Assessment as AssessmentIcon,
  CalendarMonth as CalendarIcon,
  TrendingUp as TrendingUpIcon,
  Close as CloseIcon
} from '@mui/icons-material';
import axios from 'axios';
import { getApiUrl } from '../config/api';
import { ResponsiveContainer, Tooltip as RechartsTooltip, BarChart, Bar, XAxis, YAxis, CartesianGrid } from 'recharts';

const BudgetPage = () => {
  const [budgets, setBudgets] = useState<any[]>([]);
  const [categories, setCategories] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  
  // Create Dialog State
  const [openCreate, setOpenCreate] = useState(false);
  const [createFormData, setCreateFormData] = useState({
    name: '',
    academicYear: new Date().getFullYear(),
    term: 'TERM_1',
    startDate: new Date().toISOString().split('T')[0],
    endDate: new Date(new Date().setMonth(new Date().getMonth() + 4)).toISOString().split('T')[0],
  });
  const [newBudgetItems, setNewBudgetItems] = useState([{ category: '', amount: '', description: '' }]);

  // Report Dialog State
  const [selectedReport, setSelectedReport] = useState<any>(null);

  const fetchInitialData = async () => {
    try {
      setLoading(true);
      const userInfo = localStorage.getItem('userInfo');
      const token = userInfo ? JSON.parse(userInfo).token : null;
      const headers = { Authorization: `Bearer ${token}` };

      const [budRes, catRes] = await Promise.all([
        axios.get(getApiUrl('budgets'), { headers }),
        axios.get(getApiUrl('expenses/categories'), { headers })
      ]);

      setBudgets(budRes.data);
      setCategories(catRes.data);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load budgets');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchInitialData();
  }, []);

  const handleCreateBudget = async () => {
    try {
      const userInfo = localStorage.getItem('userInfo');
      const token = userInfo ? JSON.parse(userInfo).token : null;
      const headers = { Authorization: `Bearer ${token}` };

      const payload = {
        ...createFormData,
        items: newBudgetItems.filter(item => item.category && item.amount)
      };

      await axios.post(getApiUrl('budgets'), payload, { headers });
      setOpenCreate(false);
      // Reset form
      setNewBudgetItems([{ category: '', amount: '', description: '' }]);
      setCreateFormData({
        name: '',
        academicYear: new Date().getFullYear(),
        term: 'TERM_1',
        startDate: new Date().toISOString().split('T')[0],
        endDate: new Date(new Date().setMonth(new Date().getMonth() + 4)).toISOString().split('T')[0],
      });
      fetchInitialData();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to create budget');
    }
  };

  const fetchReport = async (budgetId: string) => {
    try {
      const userInfo = localStorage.getItem('userInfo');
      const token = userInfo ? JSON.parse(userInfo).token : null;
      const headers = { Authorization: `Bearer ${token}` };

      const res = await axios.get(getApiUrl(`budgets/${budgetId}/vs-actual`), { headers });
      setSelectedReport(res.data);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load report');
    }
  };

  const addItemRow = () => {
    setNewBudgetItems([...newBudgetItems, { category: '', amount: '', description: '' }]);
  };

  const removeItemRow = (index: number) => {
    const list = [...newBudgetItems];
    list.splice(index, 1);
    setNewBudgetItems(list);
  };

  const updateItemRow = (index: number, field: string, value: string) => {
    const list = [...newBudgetItems];
    (list[index] as any)[field] = value;
    setNewBudgetItems(list);
  };

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', p: 8 }}><CircularProgress /></Box>;

  return (
    <Box sx={{ p: 3, maxWidth: 1400, mx: 'auto' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Box>
          <Typography variant="h4" fontWeight={900} color="#1A202C">Financial Budgets</Typography>
          <Typography variant="body1" color="text.secondary">Set and monitor academic spending goals</Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setOpenCreate(true)}
          sx={{ borderRadius: '12px', fontWeight: 700, px: 3, py: 1.5, textTransform: 'none', boxShadow: '0 4px 14px 0 rgba(0,118,255,0.39)' }}
        >
          Create New Budget
        </Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 4, borderRadius: '12px', fontWeight: 600 }}>{error}</Alert>}

      <Grid container spacing={3}>
        {budgets.map((budget: any) => (
          <Grid size={{ xs: 12, md: 6, lg: 4 }} key={budget.id}>
            <Card sx={{ 
              borderRadius: '20px', 
              border: '1px solid #E2E8F0', 
              boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.05)',
              transition: 'transform 0.2s',
              '&:hover': { transform: 'translateY(-4px)' }
            }}>
              <CardContent sx={{ p: 3 }}>
                <Stack direction="row" justifyContent="space-between" alignItems="flex-start" mb={2}>
                  <Box>
                    <Typography variant="h6" fontWeight={800} color="#2D3748">{budget.name}</Typography>
                    <Chip 
                      label={`${budget.academicYear} - ${budget.term || 'Annual'}`} 
                      size="small" 
                      sx={{ mt: 1, fontWeight: 700, bgcolor: '#EBF4FF', color: '#4C51BF' }} 
                    />
                  </Box>
                  <CalendarIcon sx={{ color: '#CBD5E0' }} />
                </Stack>

                <Box sx={{ mb: 3 }}>
                  <Typography variant="caption" color="text.secondary" fontWeight={700}>TOTAL BUDGETED</Typography>
                  <Typography variant="h4" fontWeight={900} color="#1A202C">
                    ${Number(budget.totalAmount).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                  </Typography>
                </Box>

                <Divider sx={{ mb: 2 }} />

                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Typography variant="body2" color="text.secondary">
                    {budget.items?.length || 0} Expense Categories
                  </Typography>
                  <Button 
                    size="small" 
                    startIcon={<AssessmentIcon />}
                    onClick={() => fetchReport(budget.id)}
                    sx={{ fontWeight: 700, borderRadius: '8px' }}
                  >
                    View Report
                  </Button>
                </Stack>
              </CardContent>
            </Card>
          </Grid>
        ))}

        {budgets.length === 0 && (
          <Grid size={{ xs: 12 }}>
            <Paper sx={{ p: 8, textAlign: 'center', borderRadius: '24px', border: '2px dashed #E2E8F0', bgcolor: 'transparent' }}>
              <TrendingUpIcon sx={{ fontSize: 64, color: '#CBD5E0', mb: 2 }} />
              <Typography variant="h6" color="#4A5568" fontWeight={700}>No budgets found</Typography>
              <Typography color="text.secondary" sx={{ mb: 3 }}>Start by creating a financial plan for the current term.</Typography>
              <Button variant="outlined" onClick={() => setOpenCreate(true)} sx={{ borderRadius: '10px', fontWeight: 700 }}>
                Initial Planning
              </Button>
            </Paper>
          </Grid>
        )}
      </Grid>

      {/* Create Budget Dialog */}
      <Dialog open={openCreate} onClose={() => setOpenCreate(false)} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: '20px' }}}>
        <DialogTitle sx={{ p: 3, fontWeight: 900 }}>Create Financial Budget</DialogTitle>
        <DialogContent sx={{ px: 3, py: 1 }}>
          <Grid container spacing={2} sx={{ mb: 4, mt: 1 }}>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField 
                fullWidth label="Budget Name" placeholder="e.g. Q1 Operational Budget"
                value={createFormData.name} onChange={(e) => setCreateFormData({...createFormData, name: e.target.value})}
              />
            </Grid>
            <Grid size={{ xs: 6, sm: 3 }}>
              <TextField 
                fullWidth label="Year" type="number"
                value={createFormData.academicYear} onChange={(e) => setCreateFormData({...createFormData, academicYear: Number(e.target.value)})}
              />
            </Grid>
            <Grid size={{ xs: 6, sm: 3 }}>
              <TextField 
                select fullWidth label="Term"
                value={createFormData.term} onChange={(e) => setCreateFormData({...createFormData, term: e.target.value})}
              >
                <MenuItem value="TERM_1">Term 1</MenuItem>
                <MenuItem value="TERM_2">Term 2</MenuItem>
                <MenuItem value="TERM_3">Term 3</MenuItem>
                <MenuItem value="ANNUAL">Annual</MenuItem>
              </TextField>
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField 
                fullWidth label="Start Date" type="date" InputLabelProps={{ shrink: true }}
                value={createFormData.startDate} onChange={(e) => setCreateFormData({...createFormData, startDate: e.target.value})}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField 
                fullWidth label="End Date" type="date" InputLabelProps={{ shrink: true }}
                value={createFormData.endDate} onChange={(e) => setCreateFormData({...createFormData, endDate: e.target.value})}
              />
            </Grid>
          </Grid>

          <Typography variant="subtitle2" fontWeight={800} color="#4A5568" mb={2}>BUDGET ALLOCATIONS</Typography>
          
          <Stack spacing={2}>
            {newBudgetItems.map((item, index) => (
              <Box key={index} sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
                <TextField
                  select label="Category" size="small" sx={{ flex: 1 }}
                  value={item.category} onChange={(e) => updateItemRow(index, 'category', e.target.value)}
                >
                  {categories.map((cat) => (
                    <MenuItem key={cat.id} value={cat.name}>{cat.name}</MenuItem>
                  ))}
                </TextField>
                <TextField
                  label="Amount" type="number" size="small" sx={{ width: 150 }}
                  value={item.amount} onChange={(e) => updateItemRow(index, 'amount', e.target.value)}
                />
                <TextField
                  label="Note" size="small" sx={{ flex: 1.5 }}
                  value={item.description} onChange={(e) => updateItemRow(index, 'description', e.target.value)}
                />
                <IconButton color="error" onClick={() => removeItemRow(index)} disabled={newBudgetItems.length === 1}>
                  <DeleteIcon />
                </IconButton>
              </Box>
            ))}
          </Stack>

          <Button 
            startIcon={<AddIcon />} 
            onClick={addItemRow}
            sx={{ mt: 2, fontWeight: 700, borderRadius: '8px' }}
          >
            Add Another Allocation
          </Button>
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button onClick={() => setOpenCreate(false)} sx={{ fontWeight: 700 }}>Cancel</Button>
          <Button variant="contained" onClick={handleCreateBudget} sx={{ borderRadius: '10px', fontWeight: 800, px: 4 }}>
            Finalize & Save Budget
          </Button>
        </DialogActions>
      </Dialog>

      {/* Report Modal */}
      <Dialog 
        open={Boolean(selectedReport)} 
        onClose={() => setSelectedReport(null)} 
        maxWidth="lg" 
        fullWidth 
        PaperProps={{ sx: { borderRadius: '24px', maxHeight: '90vh' }}}
      >
        {selectedReport && (
          <>
            <DialogTitle sx={{ p: 3, borderBottom: '1px solid #E2E8F0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Box>
                <Typography variant="h5" fontWeight={900}>{selectedReport.budget.name}</Typography>
                <Typography variant="body2" color="text.secondary">Variance Analysis Report</Typography>
              </Box>
              <IconButton onClick={() => setSelectedReport(null)}><CloseIcon /></IconButton>
            </DialogTitle>
            <DialogContent sx={{ p: 4 }}>
              <Grid container spacing={4}>
                {/* Summary Cards */}
                <Grid size={{ xs: 12, md: 4 }}>
                  <Card sx={{ bgcolor: '#F8FAFC', borderRadius: '16px', border: '1px solid #E2E8F0', boxShadow: 'none', height: '100%' }}>
                    <CardContent>
                      <Stack spacing={3}>
                        <Box>
                          <Typography variant="caption" color="text.secondary" fontWeight={800}>TOTAL BUDGETED</Typography>
                          <Typography variant="h4" fontWeight={900} color="#2D3748">${selectedReport.totalBudgeted.toLocaleString()}</Typography>
                        </Box>
                        <Box>
                          <Typography variant="caption" color="text.secondary" fontWeight={800}>ACTUAL SPENT</Typography>
                          <Typography variant="h4" fontWeight={900} color="#4C51BF">${selectedReport.totalActual.toLocaleString()}</Typography>
                        </Box>
                        <Box>
                          <Typography variant="caption" color="text.secondary" fontWeight={800}>UTILIZATION</Typography>
                          <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                            <Box sx={{ flex: 1, mr: 1 }}>
                              <LinearProgress 
                                variant="determinate" 
                                value={Math.min(selectedReport.overallUtilization, 100)} 
                                sx={{ height: 12, borderRadius: 6 }}
                              />
                            </Box>
                            <Typography variant="h6" fontWeight={800} color="#2D3748">
                              {Math.round(selectedReport.overallUtilization)}%
                            </Typography>
                          </Box>
                        </Box>
                      </Stack>
                    </CardContent>
                  </Card>
                </Grid>

                {/* Chart */}
                <Grid size={{ xs: 12, md: 8 }}>
                   <Box sx={{ height: 300 }}>
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={selectedReport.comparison}>
                          <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E2E8F0" />
                          <XAxis dataKey="category" axisLine={false} tickLine={false} tick={{ fontSize: 12, fill: '#718096' }} />
                          <YAxis axisLine={false} tickLine={false} tick={{ fontSize: 12, fill: '#718096' }} />
                          <RechartsTooltip 
                             cursor={{ fill: '#F7FAFC' }}
                             contentStyle={{ borderRadius: '12px', border: 'none', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1)' }}
                          />
                          <Bar dataKey="budgeted" fill="#E2E8F0" radius={[4, 4, 0, 0]} name="Budgeted" />
                          <Bar dataKey="actual" fill="#4C51BF" radius={[4, 4, 0, 0]} name="Actual" />
                        </BarChart>
                      </ResponsiveContainer>
                   </Box>
                </Grid>

                {/* Table Breakdown */}
                <Grid size={{ xs: 12 }}>
                  <TableContainer component={Paper} sx={{ boxShadow: 'none', border: '1px solid #E2E8F0', borderRadius: '16px' }}>
                    <Table>
                      <TableHead sx={{ bgcolor: '#F8FAFC' }}>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 800 }}>Category</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 800 }}>Budgeted</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 800 }}>Actual</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 800 }}>Variance</TableCell>
                          <TableCell sx={{ fontWeight: 800, width: 200 }}>Utilization</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {selectedReport.comparison.map((item: any, i: number) => (
                          <TableRow key={i}>
                            <TableCell sx={{ fontWeight: 600 }}>{item.category}</TableCell>
                            <TableCell align="right">${item.budgeted.toLocaleString()}</TableCell>
                            <TableCell align="right">${item.actual.toLocaleString()}</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 700, color: item.variance < 0 ? '#E53E3E' : '#38A169' }}>
                              {item.variance < 0 ? `-$${Math.abs(item.variance).toLocaleString()}` : `$${item.variance.toLocaleString()}`}
                            </TableCell>
                            <TableCell>
                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                  <Box sx={{ flex: 1, mr: 1 }}>
                                    <LinearProgress 
                                      variant="determinate" 
                                      value={Math.min(item.utilizationPercent, 100)} 
                                      color={item.utilizationPercent > 100 ? 'error' : item.utilizationPercent > 80 ? 'warning' : 'primary'}
                                      sx={{ borderRadius: 2 }}
                                    />
                                  </Box>
                                  <Typography variant="body2" fontWeight={700}>
                                    {Math.round(item.utilizationPercent)}%
                                  </Typography>
                                </Box>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Grid>
              </Grid>
            </DialogContent>
          </>
        )}
      </Dialog>
    </Box>
  );
};

export default BudgetPage;
